import React, { useRef, useCallback, useState, useEffect } from 'react';
import { 
  MDXEditor, 
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  BlockTypeSelect,
  Separator,
  type MDXEditorMethods,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import './MarkdownEditor.css';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  autoSave?: boolean;
  autoSaveInterval?: number;
  onAutoSave?: (content: string) => void;
  readOnly?: boolean;
  showToolbar?: boolean;
  className?: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start editing...',
  height: _height = 500,
  autoSave = true,
  autoSaveInterval = 30000,
  onAutoSave,
  readOnly = false,
  showToolbar = true,
  className: _className = '',
}) => {
  const ref = useRef<MDXEditorMethods>(null);
  const [content, setContent] = useState(value);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Update content when value prop changes
  useEffect(() => {
    if (value !== content) {
      setContent(value);
      ref.current?.setMarkdown(value);
    }
  }, [value, content]);

  // Auto-save functionality
  useEffect(() => {
    if (!autoSave || !onAutoSave || readOnly) return;

    const saveTimer = setInterval(async () => {
      if (hasUnsavedChanges) {
        try {
          await onAutoSave(content);
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, autoSaveInterval);

    return () => clearInterval(saveTimer);
  }, [autoSave, autoSaveInterval, content, hasUnsavedChanges, onAutoSave, readOnly]);

  const handleChange = useCallback((markdown: string) => {
    setContent(markdown);
    onChange(markdown);
    setHasUnsavedChanges(true);
  }, [onChange]);







  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* MDXEditor */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <MDXEditor
          ref={ref}
          markdown={content}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          plugins={[
            // Core plugins
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            markdownShortcutPlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            imagePlugin(),
            tablePlugin(),
            // Toolbar plugin with custom layout
            ...(showToolbar ? [toolbarPlugin({
              toolbarContents: () => (
                <>
                  <BlockTypeSelect />
                  <Separator />
                  <BoldItalicUnderlineToggles />
                  <Separator />
                  <ListsToggle />
                  <Separator />
                  <CreateLink />
                  <InsertImage />
                  <Separator />
                  <InsertTable />
                  <InsertThematicBreak />
                </>
              ),
            })] : []),
          ]}
          className="mdx-editor"
          contentEditableClassName="mdx-editor-content"
        />
      </div>

      {/* Keyboard Shortcuts Help */}
      {!readOnly && (
        <div className="editor-shortcuts-help">
          <details>
            <summary>Keyboard Shortcuts</summary>
            <div className="shortcuts-grid">
              <div><kbd>Ctrl</kbd>+<kbd>B</kbd> Bold</div>
              <div><kbd>Ctrl</kbd>+<kbd>I</kbd> Italic</div>
              <div><kbd>Ctrl</kbd>+<kbd>U</kbd> Underline</div>
              <div><kbd>Ctrl</kbd>+<kbd>K</kbd> Link</div>
              <div><kbd>Ctrl</kbd>+<kbd>Z</kbd> Undo</div>
              <div><kbd>Ctrl</kbd>+<kbd>Y</kbd> Redo</div>
              <div><kbd>Ctrl</kbd>+<kbd>S</kbd> Save</div>
              <div><kbd># </kbd> Heading</div>
              <div><kbd>- </kbd> List item</div>
              <div><kbd>{'> '}</kbd> Quote</div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default MarkdownEditor;